import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';

// ── Tipos ────────────────────────────────────────────────────────────────────
type PerfilData = {
  id: number; nombre: string; apellido: string; email: string; telefono: string;
  nro_documento: string; calle: string; nro_puerta: string; piso_depto: string;
  entre_calles: string; barrio_nombre: string;
  municipio_nombre: string; avatar_url: string | null;
  tiene_foto_anverso: boolean; tiene_foto_reverso: boolean;
  foto_solicitada: boolean;
  verificado: number | boolean;
  stats: { total_reclamos: number; activos: number; resueltos: number } | null;
};

// Mismo tipo que Register.tsx — objUrl es un dataUrl de FileReader (no blob URL)
type FotoState = { file: File; objUrl: string };

function fotoToBlob(foto: FotoState, grados = 0, maxPx = 1200): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Escalar preservando el aspecto real (lado mayor ≤ maxPx). NO tocar el
      // aspecto en función del giro: la imagen siempre se dibuja con su w×h real;
      // la rotación la resuelve el canvas intercambiando sus dimensiones.
      let w = img.naturalWidth, h = img.naturalHeight;
      if (w > maxPx || h > maxPx) {
        if (w >= h) { h = Math.round(h * maxPx / w); w = maxPx; }
        else        { w = Math.round(w * maxPx / h); h = maxPx; }
      }
      // Canvas: al girar 90/270 se intercambian ancho y alto.
      const swapAxes = grados % 180 !== 0;
      const cw = swapAxes ? h : w;
      const ch = swapAxes ? w : h;
      const canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      const ctx = canvas.getContext('2d')!;
      ctx.translate(cw / 2, ch / 2);
      ctx.rotate((grados * Math.PI) / 180);
      ctx.drawImage(img, -w / 2, -h / 2, w, h);
      canvas.toBlob(
        (b) => b ? resolve(b) : reject(new Error('canvas vacío')),
        'image/jpeg', 0.75
      );
    };
    img.onerror = () => reject(new Error('no se pudo cargar la imagen'));
    img.src = foto.objUrl;
  });
}

// Blob → dataURL. Se usa para la miniatura post-subida: la CSP de la app
// (img-src) permite data: pero NO blob:, así que un URL.createObjectURL daría
// ícono roto hasta refrescar. Un data: URL carga siempre.
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error('no se pudo leer el blob'));
    r.readAsDataURL(blob);
  });
}

type Contacto = {
  id: number; nombre: string; telefono: string;
  relacion: string | null; orden: number;
  optin_estado: 'pendiente' | 'confirmado' | 'rechazado';
};

type ContactoForm = {
  id?: number; nombre: string; telefono: string; relacion: string; orden: number;
};


// ── Componentes pequeños ──────────────────────────────────────────────────────
function Row({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3.5">
      <span className="text-lg leading-none mt-0.5 flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-400 font-semibold mb-0.5">{label}</p>
        <p className="text-base text-gray-800 font-medium break-words">{value}</p>
      </div>
    </div>
  );
}

function MenuBtn({ icon, color, label, sub, onClick, disabled }: {
  icon: string; color: string; label: string; sub: string;
  onClick: () => void; disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-4 rounded-2xl px-4 py-3.5 text-left disabled:opacity-50"
      style={{ backgroundColor: '#ffffff', border: '1px solid #6e6b6b' }}
    >
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
           style={{ backgroundColor: color + '22', border: `1.5px solid ${color}55` }}>
        <span className="text-2xl leading-none">{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm leading-tight" style={{ color: '#303030' }}>{label}</p>
        <p className="text-xs mt-0.5 leading-tight" style={{ color: '#64748b' }}>{sub}</p>
      </div>
      <span className="text-xl font-bold flex-shrink-0" style={{ color }}>›</span>
    </button>
  );
}

const OPTIN_BADGE: Record<string, { label: string; cls: string }> = {
  confirmado: { label: 'Confirmado',  cls: 'bg-green-100 text-green-700' },
  pendiente:  { label: 'Pendiente',   cls: 'bg-amber-100 text-amber-700' },
  rechazado:  { label: 'No aceptó',   cls: 'bg-red-100 text-red-600'    },
};

// ── Pantalla principal ────────────────────────────────────────────────────────
export default function Perfil() {
  const nav       = useNavigate();
  const ciudadano = useAuth((s) => s.ciudadano);
  const logout    = useAuth((s) => s.logout);

  const [perfil,    setPerfil]    = useState<PerfilData | null>(null);
  const [contactos, setContactos] = useState<Contacto[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState<string | null>(null);
  const [reenviandoVerif, setReenviandoVerif] = useState(false);
  const [msgVerif,        setMsgVerif]        = useState<string | null>(null);
  const [avatarTs,   setAvatarTs]  = useState(Date.now());
  const [subiendoAv, setSubiendoAv] = useState(false);
  const avatarInputRef             = React.useRef<HTMLInputElement>(null);


  // Formulario de contacto (bottom sheet)
  const [formCtc,    setFormCtc]    = useState<ContactoForm | null>(null);
  const [guardando,  setGuardando]  = useState(false);
  const [errCtc,     setErrCtc]     = useState<string | null>(null);

  // Fotos DNI
  const [fotoPanel,      setFotoPanel]      = useState(false);
  const [fotoAnverso,    setFotoAnverso]    = useState<FotoState | null>(null);
  const [fotoReverso,    setFotoReverso]    = useState<FotoState | null>(null);
  const [rotacion,       setRotacion]       = useState<{ anverso: number; reverso: number }>({ anverso: 0, reverso: 0 });
  const [subiendoFoto,   setSubiendoFoto]   = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errFoto,        setErrFoto]        = useState<string | null>(null);
  // Blob URLs de las fotos DNI (cargadas desde servidor o tras subida)
  const [dniBlobUrls,    setDniBlobUrls]    = useState<{ anverso?: string; reverso?: string }>({});
  const dniBlobRefs = useRef<{ anverso?: string; reverso?: string }>({});

  // Baja de cuenta
  const [bajaPanelOpen, setBajaPanelOpen] = useState(false);
  const [bajaPass,      setBajaPass]      = useState('');
  const [bajando,       setBajando]       = useState(false);
  const [errBaja,       setErrBaja]       = useState<string | null>(null);

  const cargar = useCallback(() => {
    setLoading(true);
    Promise.all([
      api<PerfilData>('/perfil'),
      api<Contacto[]>('/seguridad?action=contactos_emergencia_list').catch(() => []),
    ]).then(([p, c]) => {
      setPerfil(p);
      setContactos(Array.isArray(c) ? c : []);
    }).catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Cargar fotos DNI desde el servidor cuando el perfil indica que existen
  useEffect(() => {
    if (!perfil) return;
    const lados: ('anverso' | 'reverso')[] = [];
    if (perfil.tiene_foto_anverso && !dniBlobRefs.current.anverso) lados.push('anverso');
    if (perfil.tiene_foto_reverso && !dniBlobRefs.current.reverso)  lados.push('reverso');
    if (!lados.length) return;
    lados.forEach(async (lado) => {
      try {
        const d = await api<{ src: string }>(`/perfil?action=foto_dni&lado=${lado}`);
        dniBlobRefs.current[lado] = d.src;
        setDniBlobUrls((prev) => ({ ...prev, [lado]: d.src }));
      } catch {}
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfil?.tiene_foto_anverso, perfil?.tiene_foto_reverso]);

  async function cargarContactos() {
    const c = await api<Contacto[]>('/seguridad?action=contactos_emergencia_list').catch(() => []);
    setContactos(Array.isArray(c) ? c : []);
  }

  async function guardarContacto() {
    if (!formCtc) return;
    if (!formCtc.nombre.trim()) return setErrCtc('El nombre es obligatorio');
    if (!formCtc.telefono.trim()) return setErrCtc('El teléfono es obligatorio');
    setGuardando(true); setErrCtc(null);
    try {
      await api('/seguridad?action=contacto_emergencia_guardar', {
        method: 'POST',
        body: JSON.stringify({
          id:       formCtc.id,
          nombre:   formCtc.nombre.trim(),
          telefono: formCtc.telefono.trim(),
          relacion: formCtc.relacion.trim() || null,
          orden:    formCtc.orden,
        }),
      });
      setFormCtc(null);
      await cargarContactos();
    } catch (e: any) {
      setErrCtc(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  }

  async function eliminarContacto(id: number) {
    if (!confirm('¿Eliminar este contacto de emergencia?')) return;
    try {
      await api('/seguridad?action=contacto_emergencia_eliminar', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      await cargarContactos();
    } catch (e: any) {
      alert(e.message || 'Error al eliminar');
    }
  }

  async function solicitarBaja() {
    if (!bajaPass.trim()) return setErrBaja('Ingresá tu contraseña para confirmar');
    setBajando(true); setErrBaja(null);
    try {
      await api('/perfil?action=cuenta', {
        method: 'DELETE',
        body: JSON.stringify({ password: bajaPass }),
      });
      logout();
      nav('/login', { replace: true });
    } catch (e: any) {
      setErrBaja(e.message || 'No se pudo procesar la baja');
    } finally {
      setBajando(false);
    }
  }

  async function onAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('La imagen no puede superar 5 MB'); return; }
    const fd = new FormData();
    fd.append('avatar', file);
    setSubiendoAv(true);
    try {
      const res = await api<{ avatar_url: string }>('/perfil?action=avatar', { method: 'POST', body: fd });
      setPerfil((p) => p ? { ...p, avatar_url: res.avatar_url } : p);
      setAvatarTs(Date.now());
    } catch (ex: any) {
      alert(ex.message || 'No se pudo actualizar la foto');
    } finally {
      setSubiendoAv(false);
      e.target.value = '';
    }
  }

  function onFotoDni(e: React.ChangeEvent<HTMLInputElement>, lado: 'anverso' | 'reverso') {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { setErrFoto('La foto no puede superar 25 MB'); return; }
    setErrFoto(null);
    // Igual que Register.tsx: FileReader → dataUrl guardado en objUrl
    const reader = new FileReader();
    reader.onload = () => {
      const objUrl = reader.result as string;
      if (lado === 'anverso') setFotoAnverso({ file, objUrl });
      else                    setFotoReverso({ file, objUrl });
    };
    reader.onerror = () => setErrFoto('Error al leer el archivo');
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function reenviarVerif() {
    setReenviandoVerif(true); setMsgVerif(null);
    try {
      await api('/auth?action=reenviar_verificacion', { method: 'POST' });
      setMsgVerif('Te enviamos el email. Revisá tu casilla (y el spam).');
    } catch (e: any) {
      setMsgVerif(e?.message || 'No se pudo enviar. Probá de nuevo.');
    } finally {
      setReenviandoVerif(false);
    }
  }

  async function subirFotosDni() {
    if (!fotoAnverso && !fotoReverso) { setErrFoto('Seleccioná al menos una foto'); return; }
    setSubiendoFoto(true); setErrFoto(null); setUploadProgress(5);
    const nuevosBlobs: { anverso?: string; reverso?: string } = {};
    try {
      const pares: ['anverso' | 'reverso', FotoState | null][] = [
        ['anverso', fotoAnverso],
        ['reverso', fotoReverso],
      ];
      const total = pares.filter(([, f]) => f !== null).length;
      let done = 0;
      for (const [lado, foto] of pares) {
        if (!foto) continue;
        let blob: Blob;
        try { blob = await fotoToBlob(foto, rotacion[lado]); }
        catch { setErrFoto('No se pudo procesar la imagen. Probá con otra foto.'); return; }
        const fd = new FormData();
        fd.append('foto', blob, `${lado}.jpg`);
        setUploadProgress(10 + Math.round((done / total) * 80));
        await api(`/perfil?action=foto_dni&lado=${lado}`, { method: 'POST', body: fd });
        done++;
        setUploadProgress(10 + Math.round((done / total) * 80));
        // Mostrar el blob ROTADO que se subió (no el original sin rotar), para
        // que la miniatura en la app coincida con lo que quedó en el servidor.
        // data: URL (no blob:) porque la CSP de la app bloquea blob: en img-src.
        nuevosBlobs[lado] = await blobToDataUrl(blob);
      }
      setUploadProgress(100);
      Object.assign(dniBlobRefs.current, nuevosBlobs);
      setDniBlobUrls((prev) => ({ ...prev, ...nuevosBlobs }));
      setPerfil((p) => p ? {
        ...p,
        tiene_foto_anverso: p.tiene_foto_anverso || !!fotoAnverso,
        tiene_foto_reverso: p.tiene_foto_reverso || !!fotoReverso,
        foto_solicitada: false,
      } : p);
      setFotoAnverso(null); setFotoReverso(null);
      setRotacion({ anverso: 0, reverso: 0 });
      setFotoPanel(false); setUploadProgress(0);
    } catch (e: any) {
      setErrFoto(e.message || 'Error al subir la foto');
      setUploadProgress(0);
    } finally {
      setSubiendoFoto(false);
    }
  }

  function nuevoContacto() {
    const orden = (contactos.length + 1) as 1 | 2 | 3;
    setFormCtc({ nombre: '', telefono: '', relacion: '', orden });
    setErrCtc(null);
  }

  return (
    <>
      <TopBar title="Mi perfil" back />

      <main className="max-w-md mx-auto px-4 py-5 pb-28 space-y-4">

        {/* Avatar + nombre */}
        <div className="flex flex-col items-center pt-2 pb-2">
          <div className="relative w-24 h-24 mb-3">
            <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden" style={{ border: '4px solid #c8c8c8', boxShadow: '0 0 0 2px #a0a0a0, 0 4px 12px rgba(0,0,0,0.18)' }}>
              {perfil?.avatar_url ? (
                <img src={`${perfil.avatar_url}?v=${avatarTs}`} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl leading-none">👤</span>
              )}
              {subiendoAv && (
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">...</span>
                </div>
              )}
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={onAvatarChange}
              className="absolute inset-0 opacity-0 cursor-pointer rounded-full"
              style={{ width: '100%', height: '100%' }}
            />
            <div className="absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full bg-brand-600 flex items-center justify-center pointer-events-none" style={{ border: '2px solid #f2f2f2' }}>
              <span className="text-white text-xs leading-none">📷</span>
            </div>
          </div>
          <p className="text-2xl font-extrabold text-gray-900">
            {ciudadano?.nombre} {ciudadano?.apellido}
          </p>
          {ciudadano?.municipio_nombre && (
            <p className="text-sm text-gray-400 mt-0.5">{ciudadano.municipio_nombre}</p>
          )}
          {/* Stats */}
          {perfil?.stats && perfil.stats.total_reclamos > 0 && (
            <div className="flex gap-4 mt-3">
              <StatChip val={perfil.stats.total_reclamos} label="Reclamos" />
              <StatChip val={perfil.stats.activos}        label="Activos" color="text-amber-600" />
              <StatChip val={perfil.stats.resueltos}      label="Resueltos" color="text-green-600" />
            </div>
          )}
        </div>

        {loading && <p className="text-center text-gray-400 text-sm py-4">Cargando datos…</p>}
        {err     && <p className="text-center text-red-500 text-sm py-4">{err}</p>}

        {perfil && (
          <>
            {/* Aviso: email sin verificar — recordatorio + reenviar */}
            {perfil.email && !perfil.verificado && (
              <div className="rounded-2xl p-4" style={{ background: '#fef9c3', border: '1px solid #fde68a' }}>
                <div className="flex items-start gap-3">
                  <span className="text-xl leading-none">✉️</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm" style={{ color: '#854d0e' }}>Verificá tu email</p>
                    <p className="text-xs mt-0.5 leading-snug" style={{ color: '#a16207' }}>
                      Te enviamos un enlace a <strong>{perfil.email}</strong>. Tocá ahí para confirmar tu casilla.
                    </p>
                    {msgVerif && (
                      <p className="text-xs mt-1.5 font-semibold" style={{ color: '#166534' }}>{msgVerif}</p>
                    )}
                    <button
                      onClick={reenviarVerif}
                      disabled={reenviandoVerif}
                      className="mt-2 text-xs font-bold rounded-lg px-3 py-1.5 disabled:opacity-50"
                      style={{ background: '#854d0e', color: '#fff' }}
                    >
                      {reenviandoVerif ? 'Enviando…' : 'Reenviar email'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Datos personales */}
            <div className="bg-white border border-app rounded-2xl divide-y divide-gray-100">
              <Row icon="📧" label="Email"     value={perfil.email || '–'}    />
              <Row icon="📱" label="Teléfono"  value={perfil.telefono || '–'} />
              <Row icon="🪪" label="DNI"       value={perfil.nro_documento || '–'} />
              {(perfil.calle || perfil.barrio_nombre) && (
                <Row
                  icon="📍"
                  label="Domicilio"
                  value={[perfil.calle, perfil.nro_puerta, perfil.barrio_nombre].filter(Boolean).join(' ')}
                />
              )}
            </div>

            {/* Contactos de emergencia */}
            <div className="bg-white border border-app rounded-2xl overflow-hidden">
              <div className="px-4 pt-4 pb-2">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                  Contactos de emergencia
                </p>
                <p className="text-xs text-gray-500 leading-snug">
                  Hasta 3 personas que serán notificadas si activás el botón de pánico.
                  Recibirán un WhatsApp para aceptar ser tu contacto.
                </p>
              </div>

              {contactos.length === 0 && (
                <div className="px-4 pb-3 pt-1">
                  <p className="text-sm text-gray-400 italic">Sin contactos configurados.</p>
                </div>
              )}

              {contactos.map((c) => {
                const badge = OPTIN_BADGE[c.optin_estado] ?? OPTIN_BADGE.pendiente;
                return (
                  <div key={c.id} className="border-t border-gray-100 px-4 py-3 flex items-start gap-3">
                    <span className="text-xl leading-none mt-0.5">👤</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-base text-gray-900">{c.nombre}</p>
                      <p className="text-sm text-gray-500">{c.telefono}</p>
                      {c.relacion && <p className="text-xs text-gray-400">{c.relacion}</p>}
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-1 inline-block ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button
                        onClick={() => { setFormCtc({ id: c.id, nombre: c.nombre, telefono: c.telefono, relacion: c.relacion ?? '', orden: c.orden }); setErrCtc(null); }}
                        className="text-xs text-brand-600 font-semibold px-2 py-1 rounded-lg bg-brand-50"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => eliminarContacto(c.id)}
                        className="text-xs text-red-500 font-semibold px-2 py-1 rounded-lg bg-red-50"
                      >
                        Borrar
                      </button>
                    </div>
                  </div>
                );
              })}

              {contactos.length < 3 && (
                <div className="border-t border-gray-100 px-4 py-3">
                  <button
                    onClick={nuevoContacto}
                    className="text-sm font-semibold"
                    style={{ color: '#32329f' }}
                  >
                    + Agregar contacto
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Fotos del documento */}
        {perfil && (
          <div className="bg-white border border-app rounded-2xl overflow-hidden">
            <div className="px-4 pt-4 pb-3">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                Fotos del documento
              </p>
              <p className="text-xs text-gray-500 leading-snug mb-3">
                Se usan para verificar tu identidad. Podés actualizarlas en cualquier momento.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {(['anverso', 'reverso'] as const).map((lado) => {
                  const tiene    = lado === 'anverso' ? perfil.tiene_foto_anverso : perfil.tiene_foto_reverso;
                  const preview  = dniBlobUrls[lado];
                  const label    = lado === 'anverso' ? 'Frente' : 'Dorso';
                  return (
                    <div key={lado} className="rounded-xl overflow-hidden text-center"
                         style={{ background: tiene ? '#f0fdf4' : '#fff8f8', border: `1.5px solid ${tiene ? '#86efac' : '#fca5a5'}` }}>
                      {preview ? (
                        <img src={preview} alt={label}
                             style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                      ) : null}
                      <div className="p-2">
                        <p className="text-base mb-0.5">{tiene ? '✅' : '⚠️'}</p>
                        <p className="text-xs font-semibold" style={{ color: tiene ? '#16a34a' : '#ef4444' }}>{label}</p>
                        <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>{tiene ? 'Subida' : 'No subida'}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {(!perfil.tiene_foto_anverso || !perfil.tiene_foto_reverso || perfil.foto_solicitada) && (
              <div className="border-t border-gray-100 px-4 py-3">
                {perfil.foto_solicitada && (
                  <p className="text-xs text-amber-600 font-semibold mb-2">
                    ⚠ El municipio solicitó que actualices tus fotos de documento.
                  </p>
                )}
                <button
                  onClick={() => { setFotoPanel(true); setErrFoto(null); setFotoAnverso(null); setFotoReverso(null); setRotacion({ anverso: 0, reverso: 0 }); }}
                  className="text-sm font-semibold"
                  style={{ color: '#32329f' }}
                >
                  {perfil.tiene_foto_anverso && perfil.tiene_foto_reverso ? '↺ Actualizar fotos' : '+ Subir fotos'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Acciones — mismo estilo que pantalla principal */}
        <div className="space-y-2.5">
          <MenuBtn
            icon="✏️" color="#32329f"
            label="Editar datos personales"
            sub="Nombre, domicilio, teléfono, email"
            onClick={() => nav('/editar-datos')}
            disabled={!perfil}
          />
          <MenuBtn
            icon="🔐" color="#32329f"
            label="Cambiar contraseña"
            sub="Actualizar tu clave de acceso"
            onClick={() => nav('/cambiar-password')}
          />
        </div>

        {/* Baja de cuenta */}
        <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#ffffff', border: '1px solid #6e6b6b' }}>
          <button
            onClick={() => { setBajaPanelOpen((v) => !v); setErrBaja(null); setBajaPass(''); }}
            className="w-full px-4 py-3.5 flex items-center gap-4 text-left"
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                 style={{ backgroundColor: '#64748b22', border: '1.5px solid #64748b55' }}>
              <span className="text-2xl leading-none">🗑️</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm leading-tight" style={{ color: '#303030' }}>Solicitar baja de cuenta</p>
              <p className="text-xs mt-0.5 leading-tight" style={{ color: '#64748b' }}>Ley 25.326 · Derecho de supresión</p>
            </div>
            <span className="text-sm font-bold flex-shrink-0" style={{ color: '#64748b' }}>{bajaPanelOpen ? '▲' : '▼'}</span>
          </button>

          {bajaPanelOpen && (
            <div className="border-t px-4 pb-5 pt-4 space-y-3" style={{ borderColor: '#e2e8f0' }}>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-sm text-amber-800 font-semibold mb-1">⚠ Esta acción no se puede deshacer</p>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Tus datos personales serán eliminados o anonimizados conforme a la Ley 25.326
                  de Protección de Datos Personales. Si tenés reclamos activos, quedarán anonimizados
                  para preservar el historial municipal.
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1.5">Confirmá con tu contraseña:</label>
                <input
                  type="password" value={bajaPass}
                  onChange={(e) => setBajaPass(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-xl px-4 py-3 text-sm outline-none"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #6e6b6b', color: '#303030' }}
                />
              </div>
              {errBaja && <p className="text-sm text-red-500">{errBaja}</p>}
              <button onClick={solicitarBaja} disabled={bajando}
                className="w-full bg-red-600 disabled:opacity-50 text-white rounded-xl py-3 font-bold text-sm">
                {bajando ? 'Procesando…' : 'Eliminar mi cuenta y mis datos'}
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-300 pt-1">
          CiudadApp · {ciudadano?.municipio_nombre}
        </p>
      </main>

      {/* Bottom sheet: fotos DNI */}
      {fotoPanel && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setFotoPanel(false)} />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl"
               style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <div className="px-5 pt-5 pb-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-bold text-lg text-gray-900">Fotos del documento</p>
                <button onClick={() => setFotoPanel(false)} className="text-gray-400 text-2xl leading-none">×</button>
              </div>
              <p className="text-xs text-gray-500 leading-snug">
                Sacá fotos nítidas del frente y dorso de tu DNI. Se comprimen automáticamente.
              </p>

              {(['anverso', 'reverso'] as const).map((lado) => {
                const foto  = lado === 'anverso' ? fotoAnverso : fotoReverso;
                const label = lado === 'anverso' ? 'Frente del documento' : 'Dorso del documento';
                const clearFn = () => { (lado === 'anverso' ? setFotoAnverso : setFotoReverso)(null); setRotacion((r) => ({ ...r, [lado]: 0 })); };
                return (
                  <div key={lado}>
                    <p className="text-sm font-semibold text-gray-700 mb-2">{label}</p>
                    {foto ? (
                      <div className="relative rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center" style={{ height: 160 }}>
                        <img src={foto.objUrl} alt={lado}
                             style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain',
                                      transform: `rotate(${rotacion[lado]}deg)`, transition: 'transform .25s' }} />
                        <button onClick={clearFn}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white text-sm flex items-center justify-center leading-none"
                        >×</button>
                        <button
                          onClick={() => setRotacion((r) => ({ ...r, [lado]: (r[lado] + 90) % 360 }))}
                          className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center text-base leading-none"
                          title="Girar"
                        >↻</button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-5 cursor-pointer gap-1"
                               style={{ background: '#f8fafc' }}>
                          <span className="text-2xl">📷</span>
                          <span className="text-xs font-semibold text-gray-500">Cámara</span>
                          <input type="file" accept="image/*" capture="environment"
                                 style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                                 onChange={(e) => onFotoDni(e, lado)} />
                        </label>
                        <label className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-5 cursor-pointer gap-1"
                               style={{ background: '#f8fafc' }}>
                          <span className="text-2xl">🖼️</span>
                          <span className="text-xs font-semibold text-gray-500">Galería</span>
                          <input type="file" accept="image/*"
                                 style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                                 onChange={(e) => onFotoDni(e, lado)} />
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}

              {errFoto && <p className="text-sm text-red-500">{errFoto}</p>}

              <button
                onClick={subirFotosDni}
                disabled={subiendoFoto || (!fotoAnverso && !fotoReverso)}
                className="w-full text-white rounded-xl font-bold text-base disabled:opacity-40 overflow-hidden relative"
                style={{ backgroundColor: '#32329f', minHeight: 52 }}
              >
                {subiendoFoto ? (
                  <div className="px-4 py-3.5">
                    <p className="text-sm mb-2">Subiendo…</p>
                    <div className="w-full rounded-full overflow-hidden" style={{ height: 6, background: 'rgba(255,255,255,0.25)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${uploadProgress}%`,
                          background: 'linear-gradient(90deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.6) 100%)',
                          backgroundSize: '200% 100%',
                          animation: uploadProgress < 100 ? 'gaugeShine 1.4s linear infinite' : 'none',
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <span className="py-3.5 block">Guardar fotos</span>
                )}
              </button>
              <style>{`
                @keyframes gaugeShine {
                  0%   { background-position: 100% 0; }
                  100% { background-position: -100% 0; }
                }
              `}</style>
            </div>
          </div>
        </>
      )}

      {/* Bottom sheet: form de contacto */}
      {formCtc && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setFormCtc(null)}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl"
               style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <div className="px-5 pt-5 pb-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-bold text-lg text-gray-900">
                  {formCtc.id ? 'Editar contacto' : 'Agregar contacto'}
                </p>
                <button
                  onClick={() => setFormCtc(null)}
                  className="text-gray-400 text-2xl leading-none"
                >×</button>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1.5">Nombre</label>
                <input
                  type="text"
                  value={formCtc.nombre}
                  onChange={(e) => setFormCtc((p) => p ? { ...p, nombre: e.target.value } : p)}
                  placeholder="Nombre completo"
                  className="w-full rounded-xl px-4 py-3 text-sm outline-none"
                  style={{ backgroundColor: '#f8f8f8', border: '1px solid #6e6b6b', color: '#303030' }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1.5">Teléfono (con código de área)</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={formCtc.telefono}
                  onChange={(e) => setFormCtc((p) => p ? { ...p, telefono: e.target.value } : p)}
                  placeholder="Ej: 2993012345"
                  className="w-full rounded-xl px-4 py-3 text-sm outline-none"
                  style={{ backgroundColor: '#f8f8f8', border: '1px solid #6e6b6b', color: '#303030' }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1.5">Relación (opcional)</label>
                <input
                  type="text"
                  value={formCtc.relacion}
                  onChange={(e) => setFormCtc((p) => p ? { ...p, relacion: e.target.value } : p)}
                  placeholder="Mamá, Pareja, Vecino…"
                  className="w-full rounded-xl px-4 py-3 text-sm outline-none"
                  style={{ backgroundColor: '#f8f8f8', border: '1px solid #6e6b6b', color: '#303030' }}
                />
              </div>

              {errCtc && <p className="text-sm text-red-500">{errCtc}</p>}

              <button
                onClick={guardarContacto}
                disabled={guardando}
                className="w-full text-white rounded-xl py-3.5 font-bold text-base disabled:opacity-50"
                style={{ backgroundColor: '#32329f' }}
              >
                {guardando ? 'Guardando…' : formCtc.id ? 'Guardar cambios' : 'Agregar contacto'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function StatChip({ val, label, color = 'text-gray-700' }: { val: number; label: string; color?: string }) {
  return (
    <div className="text-center">
      <p className={`text-xl font-extrabold leading-none ${color}`}>{val}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
    </div>
  );
}
